import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Download, FileText, BarChart3, X } from 'lucide-react';
import { Converter } from 'opencc-js/cn2t';
import { Transcript } from '@/hooks/usePSAgentCall';

import {
  generateSurveyFeedback,
  formatTranscriptForAPI,
  getPriorityColor,
  DEFAULT_SURVEY_QUESTIONS,
  V5_EVALUATION_CRITERIA,
  V6_EVALUATION_CRITERIA,
  V7_EVALUATION_CRITERIA,
  V8_EVALUATION_CRITERIA,
  FeedbackItem,
} from '@/utils/psFeedbackApi';

// Simplified -> Traditional (Taiwan) converter for the Prudential TW demos (v7/v8).
const toTraditionalTW = Converter({ from: 'cn', to: 'tw' });

/**
 * Converts pronunciation-hack characters back to proper display characters.
 * The TTS uses 宝 (bǎo, 3rd tone) for correct pronunciation,
 * but we display 保 (the correct written form).
 * When `toTraditional` is set, additionally converts Simplified -> Traditional (Taiwan).
 */
const normalizeDisplayText = (text: string, toTraditional = false): string => {
  const out = text
    .replace(/宝诚/g, '保诚')    // Prudential
    .replace(/宝单/g, '保单')    // insurance policy
    .replace(/宝险/g, '保险')    // insurance
    .replace(/\[[^\[\]]*\]/g, ''); // Remove bracketed text like [감사합니다]
  return toTraditional ? toTraditionalTW(out) : out;
};

interface PSSessionFeedbackProps {
  sessionId: string | null;
  conversationId?: string | null;
  callDuration: number;
  transcript: Transcript[];
  formatTime: (seconds: number) => string;
  onStartNewSession: () => void;
  onClose?: () => void;
  version?: number;
  agentName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`feedback-tabpanel-${index}`}
      aria-labelledby={`feedback-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Format priority for display
const PRIORITY_LABELS_KO: Record<string, string> = {
  high: '높은 우선순위',
  medium: '중간 우선순위',
  low: '낮은 우선순위',
};

const PRIORITY_LABELS_JA: Record<string, string> = {
  high: '高優先度',
  medium: '中優先度',
  low: '低優先度',
};

const PRIORITY_LABELS_ZH: Record<string, string> = {
  high: '高優先',
  medium: '中優先',
  low: '低優先',
};

const formatPriority = (priority: string, version?: number): string => {
  if (version === 6) {
    return PRIORITY_LABELS_JA[priority.toLowerCase()] ?? `${priority.toUpperCase()} 優先度`;
  }
  if (version === 5) {
    return PRIORITY_LABELS_KO[priority.toLowerCase()] ?? `${priority.toUpperCase()} 우선순위`;
  }
  if (version === 7 || version === 8) {
    return PRIORITY_LABELS_ZH[priority.toLowerCase()] ?? `${priority.toUpperCase()} 優先`;
  }
  return `${priority.toUpperCase()} PRIORITY`;
};

// Extract conversation ID from room string
const extractConversationId = (fullConversationId: string): string => {
  // Handle cases like: room_agent_01k03xhsgnee786p471adr8w6w_conv_8801k2p301zaefabwyc0h0vkekbt
  // Extract the part starting from 'conv_' including the prefix
  const convIndex = fullConversationId.lastIndexOf('_conv_');
  if (convIndex !== -1) {
    return fullConversationId.substring(convIndex + 1); // Skip the underscore but keep 'conv_'
  }

  // If no '_conv_' pattern, check if it already starts with 'conv_'
  if (fullConversationId.startsWith('conv_')) {
    return fullConversationId;
  }

  // If it's just an ID without 'conv_' prefix, add it
  return `conv_${fullConversationId}`;
};

export const PSSessionFeedback: React.FC<PSSessionFeedbackProps> = ({
  sessionId,
  conversationId,
  callDuration,
  transcript,
  formatTime,
  onStartNewSession,
  onClose,
  version,
  agentName,
}) => {
  const isKorean = version === 5;
  const isJapanese = version === 6;
  const isChinese = version === 7 || version === 8;
  const [activeTab, setActiveTab] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadCooldown, setIsDownloadCooldown] = useState(true);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [_isPollingAudio, setIsPollingAudio] = useState(false);
  const [_pollCount, setPollCount] = useState(0);

  const checkConversationAudio = useCallback(
    async (convId: string, attemptNumber: number): Promise<boolean> => {
      try {
        console.log(
          `[Audio Polling] Attempt #${attemptNumber} - Checking has_audio for conversation:`,
          convId,
        );

        const apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
        if (!apiKey) {
          throw new Error('Missing ElevenLabs API key');
        }

        const actualConversationId = extractConversationId(convId);

        const response = await fetch(
          `https://api.elevenlabs.io/v1/convai/conversations/${actualConversationId}`,
          {
            headers: {
              'xi-api-key': apiKey,
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to check conversation status: ${response.status}`,
          );
        }

        const data = await response.json();
        const hasAudioResult = data.has_audio || false;
        console.log(
          `[Audio Polling] Attempt #${attemptNumber} - has_audio:`,
          hasAudioResult,
        );

        return hasAudioResult;
      } catch (err) {
        console.error(
          `[Audio Polling] Attempt #${attemptNumber} - Error:`,
          err,
        );
        return false;
      }
    },
    [],
  );

  const analyzeFeedback = useCallback(async () => {
    if (transcript.length === 0) {
      console.warn('No transcript available for analysis');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const formattedTranscript = formatTranscriptForAPI(transcript);

      // Debug: Log what we're sending to the API
      console.log('🔍 PS Survey Analysis Debug:');
      console.log('Transcript length:', transcript.length);
      console.log('Formatted transcript:', formattedTranscript);

      // Check if we have meaningful conversation content
      const customerMessages = transcript.filter((t) => t.source === 'user');
      const trivialWords = isJapanese
        ? ['はい', 'ええ', 'いいえ', 'そうです', 'どうも', 'はいはい']
        : isKorean
          ? ['네', '예', '응', '아니요', '아니', '네네', '예예']
          : isChinese
            ? ['是', '是的', '對', '對的', '好', '好的', '嗯', '沒有', '不是', '可以']
            : ['hello', 'hi', 'yes', 'no', 'ok', 'okay'];
      const minCharLength = (isJapanese || isKorean || isChinese) ? 4 : 10;
      const meaningfulCustomerResponses = customerMessages.filter(
        (t) =>
          t.message.trim().length > minCharLength &&
          !trivialWords.includes(t.message.toLowerCase().trim()),
      );

      console.log('Customer messages:', customerMessages.length);
      console.log(
        'Meaningful customer responses:',
        meaningfulCustomerResponses.length,
      );

      // If insufficient meaningful conversation, show a different state
      if (meaningfulCustomerResponses.length < 3) {
        console.log(
          '⚠️ Insufficient conversation data for meaningful analysis',
        );
        setFeedbackItems([]);
        setHasAnalyzed(true);
        setAnalysisError(
          'Insufficient conversation data. The survey appears incomplete - most questions were not answered in detail.',
        );
        return;
      }

      const surveyQuestions = isJapanese
        ? V6_EVALUATION_CRITERIA
        : isKorean
          ? V5_EVALUATION_CRITERIA
          : version === 8
            ? V8_EVALUATION_CRITERIA
            : version === 7
              ? V7_EVALUATION_CRITERIA
              : DEFAULT_SURVEY_QUESTIONS;
      const response = await generateSurveyFeedback(
        surveyQuestions,
        formattedTranscript,
        version,
      );
      console.log('API Response:', response);
      setFeedbackItems(response.feedbackItems);
      setHasAnalyzed(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to analyze feedback';
      setAnalysisError(errorMessage);
      console.error('Feedback analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [transcript, isKorean, isJapanese, isChinese, version]);

  // Automatically analyze transcript when component mounts or transcript changes
  useEffect(() => {
    if (transcript.length > 0 && !hasAnalyzed) {
      analyzeFeedback();
    }
  }, [transcript, hasAnalyzed, analyzeFeedback]);

  // Start polling for audio availability when component mounts
  useEffect(() => {
    if (!conversationId || hasAudio) {
      return;
    }

    setIsPollingAudio(true);
    let currentPollCount = 0;

    const poll = async () => {
      currentPollCount++;
      setPollCount(currentPollCount);

      // Stop polling after 100 attempts (5 minutes at 3-second intervals)
      if (currentPollCount >= 100) {
        console.log(
          '[Audio Polling] Maximum attempts reached, stopping polling',
        );
        setIsPollingAudio(false);
        clearInterval(intervalId);
        return;
      }

      const audioAvailable = await checkConversationAudio(
        conversationId,
        currentPollCount,
      );

      if (audioAvailable) {
        console.log('[Audio Polling] Audio is available! Stopping polling.');
        setHasAudio(true);
        setIsPollingAudio(false);
        clearInterval(intervalId);
      }
    };

    // Initial check
    poll();

    // Set up polling every 3 seconds
    const intervalId = setInterval(poll, 3000);

    // Cleanup function
    return () => {
      clearInterval(intervalId);
      setIsPollingAudio(false);
    };
  }, [conversationId, hasAudio, checkConversationAudio]);

  // After finishing the session (when this screen mounts),
  // disable the Download button for ~20 seconds to allow backend to prepare audio
  // But if hasAudio becomes true, we can end the cooldown early
  useEffect(() => {
    setIsDownloadCooldown(true);
    const t = setTimeout(() => setIsDownloadCooldown(false), 20000);
    return () => clearTimeout(t);
  }, []);

  // End cooldown early if audio becomes available
  useEffect(() => {
    if (hasAudio && isDownloadCooldown) {
      console.log('[Audio Polling] Audio available - ending cooldown early');
      setIsDownloadCooldown(false);
    }
  }, [hasAudio, isDownloadCooldown]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleDownloadAudio = async () => {
    try {
      setIsDownloading(true);
      setDownloadError(null);

      // Validate API key from env
      const apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
      if (!apiKey) {
        throw new Error(
          'Missing ElevenLabs API key. Set VITE_ELEVENLABS_API_KEY in your environment.',
        );
      }

      // Extract the actual conversation ID from the room string
      const actualConversationId = conversationId
        ? extractConversationId(conversationId)
        : null;

      if (!actualConversationId) {
        throw new Error('No conversation ID available for download');
      }

      console.log('[Download] Full conversationId:', conversationId);
      console.log('[Download] Extracted conversationId:', actualConversationId);

      // Step 3: download using history/download with default format
      const res = await fetch(
        `https://api.elevenlabs.io/v1/convai/conversations/${actualConversationId}/audio`,
        {
          method: 'GET',
          headers: {
            'xi-api-key': apiKey,
          },
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          `Failed to download (${res.status}): ${text || 'Unknown error'}`,
        );
      }

      const blob = await res.blob();
      const contentType = res.headers.get('Content-Type') || '';
      const isZip = contentType.includes('zip');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = `ps-survey-audio-${sessionId || 'session'}`;
      a.download = isZip ? `${base}.zip` : `${base}.mp3`;
      console.log('[Download] Triggering browser download:', a.download);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Download failed';
      setDownloadError(msg);
      console.error('Download error:', e);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          p: 3,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {isJapanese ? 'セッション後フィードバック' : isKorean ? '세션 후 피드백' : isChinese ? '電話訪問後分析' : 'Post-Session Feedback'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isJapanese ? '通話時間:' : isKorean ? '통화 시간:' : isChinese ? '通話時間:' : 'Duration:'} {formatTime(callDuration)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<Download size={16} />}
              onClick={handleDownloadAudio}
              sx={{ bgcolor: '#0064CC', '&:hover': { bgcolor: '#0056b3' } }}
              disabled={isDownloading || isDownloadCooldown || !hasAudio}
            >
              {isDownloadCooldown
                ? (isJapanese ? '音声準備中…' : isKorean ? '오디오 준비 중…' : isChinese ? '音檔準備中…' : 'Preparing audio…')
                : !hasAudio
                ? (isJapanese ? '音声準備中…' : isKorean ? '오디오 준비 중…' : isChinese ? '音檔準備中…' : 'Preparing audio…')
                : isDownloading
                ? (isJapanese ? '音声ダウンロード中…' : isKorean ? '오디오 다운로드 중…' : isChinese ? '音檔下載中…' : 'Downloading audio…')
                : (isJapanese ? '音声ダウンロード' : isKorean ? '오디오 다운로드' : isChinese ? '下載音檔' : 'Download Audio')}
            </Button>
            {onClose && (
              <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                <X size={20} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3 }}>
          <Tab
            icon={<BarChart3 size={20} />}
            label={isJapanese ? 'フィードバック' : isKorean ? '피드백' : isChinese ? '分析回饋' : 'Feedback'}
            iconPosition="start"
          />
          <Tab
            icon={<FileText size={20} />}
            label={isJapanese ? '通話記録' : isKorean ? '통화 녹취록' : isChinese ? '通話逐字稿' : 'Call Transcript'}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <TabPanel value={activeTab} index={0}>
          {/* Feedback Tab */}
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">{isJapanese ? '通話フィードバック分析' : isKorean ? '통화 피드백 분석' : isChinese ? '電話訪問分析' : 'Survey Feedback Analysis'}</Typography>
              {/* Actions removed by request */}
            </Box>

            {/* Loading State */}
            {isAnalyzing && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress size={48} sx={{ color: '#0064CC', mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {isJapanese ? '通話内容を分析中' : isKorean ? '통화 내용 분석 중' : isChinese ? '正在分析通話內容' : 'Analyzing Survey Responses'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isJapanese ? 'AIが通話内容を分析しています...' : isKorean ? 'AI가 통화 내용을 분석하고 있습니다...' : isChinese ? 'AI 正在檢視通話內容，找出可能的問題...' : 'AI is reviewing the conversation for potential issues...'}
                </Typography>
              </Box>
            )}

            {/* Error State */}
            {analysisError && !isAnalyzing && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                {analysisError.includes('Insufficient conversation data') ? (
                  <>
                    <Typography
                      variant="h6"
                      color="warning.main"
                      sx={{ mb: 1 }}
                    >
                      {isJapanese ? '不完全な通話' : isKorean ? '불완전한 통화' : isChinese ? '通話內容不完整' : 'Incomplete Survey'}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}
                    >
                      {isJapanese
                        ? '通話データが不十分です。通話が不完全なようです — ほとんどの質問に詳細な回答がありませんでした。'
                        : isKorean
                          ? '통화 데이터가 부족합니다. 통화가 불완전한 것 같습니다 — 대부분의 질문에 상세한 답변이 없었습니다.'
                          : isChinese
                            ? '通話資料不足，本次通話似乎尚未完成 — 多數項目沒有詳細的回覆內容。'
                            : analysisError}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      {isJapanese
                        ? '意味のあるフィードバック分析のために、通話を最後まで完了してください。'
                        : isKorean
                          ? '의미 있는 피드백 분석을 위해 전체 통화를 완료해 주세요.'
                          : isChinese
                            ? '請完成一次完整的電話訪問，以取得有意義的分析結果。'
                            : 'Complete a full survey conversation to get meaningful feedback analysis.'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" color="error" sx={{ mb: 1 }}>
                      {isJapanese ? '分析失敗' : isKorean ? '분석 실패' : isChinese ? '分析失敗' : 'Analysis Failed'}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {analysisError}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={analyzeFeedback}
                      startIcon={<BarChart3 size={16} />}
                    >
                      {isJapanese ? '再試行' : isKorean ? '다시 시도' : isChinese ? '重新分析' : 'Try Again'}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* No Issues State */}
            {!isAnalyzing &&
              !analysisError &&
              hasAnalyzed &&
              feedbackItems.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                    {isJapanese ? '問題なし' : isKorean ? '문제 없음' : isChinese ? '未發現問題' : 'No Issues Detected'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {isJapanese ? 'すべての通話応答が満足のいくサービス品質を示しています' : isKorean ? '모든 통화 응답이 만족스러운 서비스 품질을 나타냅니다' : isChinese ? '所有通話內容皆顯示服務品質良好' : 'All survey responses indicate satisfactory service quality'}
                  </Typography>
                </Box>
              )}

            {/* Feedback Items */}
            {!isAnalyzing && !analysisError && feedbackItems.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {feedbackItems.map((item, index) => {
                  const priorityColor = getPriorityColor(item.priority);
                  return (
                    <Card
                      key={index}
                      sx={{
                        border: 1,
                        borderColor: `${priorityColor}.light`,
                        borderLeftWidth: 4,
                        borderLeftColor: `${priorityColor}.main`,
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 2,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              {normalizeDisplayText(item.questionRef, isChinese)}
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5, mb: 1 }}>
                              {normalizeDisplayText(item.problem, isChinese)}
                            </Typography>
                          </Box>
                          <Chip
                            label={formatPriority(item.priority, version)}
                            color={priorityColor}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>

                        {/* Customer Response */}
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            {isJapanese ? '顧客応答:' : isKorean ? '고객 응답:' : isChinese ? '客戶回應:' : 'Customer Response:'}
                          </Typography>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: 'grey.50',
                              borderLeft: 3,
                              borderColor: 'warning.main',
                              fontStyle: 'italic',
                            }}
                          >
                            <Typography variant="body2">
                              "{normalizeDisplayText(item.customerResponse, isChinese)}"
                            </Typography>
                          </Paper>
                        </Box>

                        {/* Required Action */}
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ mb: 1, fontWeight: 600 }}
                          >
                            {isJapanese ? '必要な対応:' : isKorean ? '필요 조치:' : isChinese ? '建議改善:' : 'Required Action:'}
                          </Typography>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: `${priorityColor}.50`,
                              border: 1,
                              borderColor: `${priorityColor}.light`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {normalizeDisplayText(item.actionRequired, isChinese)}
                            </Typography>
                          </Paper>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Call Transcript Tab */}
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6">{isJapanese ? '通話記録' : isKorean ? '통화 녹취록' : isChinese ? '通話逐字稿' : 'Conversation Transcript'}</Typography>
            </Box>

            <Card>
              <CardContent sx={{ p: 0 }}>
                {transcript.length === 0 ? (
                  <Box
                    sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}
                  >
                    <Typography>{isJapanese ? '録音された通話はありません' : isKorean ? '녹음된 통화가 없습니다' : isChinese ? '沒有通話記錄' : 'No conversation recorded'}</Typography>
                  </Box>
                ) : (
                  <List sx={{ py: 0 }}>
                    {transcript.map((entry, index) => (
                      <React.Fragment key={index}>
                        <ListItem
                          sx={{
                            py: 2,
                            bgcolor:
                              entry.source === 'ai' ? '#f8f9fa' : 'white',
                            borderLeft:
                              entry.source === 'ai'
                                ? '4px solid #0064CC'
                                : '4px solid #28a745',
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 60 }}>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor:
                                  entry.source === 'ai' ? '#0064CC' : '#28a745',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              {entry.source === 'ai' ? 'AI' : 'U'}
                            </Box>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {entry.source === 'ai' ? (agentName || 'Mei') : (isJapanese ? 'お客様' : isKorean ? '고객' : isChinese ? '客戶' : 'Customer')} •{' '}
                                  {entry.timestamp.toLocaleTimeString()}
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                  {normalizeDisplayText(entry.message, isChinese)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < transcript.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Box>
        </TabPanel>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onStartNewSession}
          sx={{ px: 4 }}
        >
          {isJapanese ? '新規通話開始' : isKorean ? '새 통화 시작' : isChinese ? '開始新通話' : 'Start New Survey'}
        </Button>
      </Box>
      {downloadError && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="error" variant="body2">
            {downloadError}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
